import { BfxBlotterOrdersContextMenuItemCancelState } from "./BfxBlotterOrdersContextMenuItemCancelState";

import { BfxBlotterOrdersContextMenuState } from "./BfxBlotterOrdersContextMenuState";
import { BfxBlotterOrdersContextMenuItem } from "./BfxBlotterOrdersContextMenuItem";

import { BfxBlotterAction } from "../bfx-base-blotter/BfxBlotterAction";

import { ContextMenuItemState } from "../ContextMenuItemState";
import { ContextMenuItemStateCommand } from "../ContextMenuItemStateCommand";
import { ContextMenuItemStateCommandCriteria } from "../ContextMenuItemStateCommandCriteria";
import { ContextMenuState } from "../ContextMenuState";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { PROP_POSTHARDCUTOFF } from "shared/cubiq-services/mediator/contextmenu/bfx-blotter-orders/impl/BfxBlotterOrdersContextMenuPrimaryService";

export class BfxBlotterOrdersContextMenuCancelItem extends BfxBlotterOrdersContextMenuItem {
  constructor() {
    super(
      BfxBlotterAction.ACTION_ORDER_CANCEL,
      "Cancel",
      BfxBlotterOrdersContextMenuCancelItem.checkAvailableFn(),
      BfxBlotterOrdersContextMenuCancelItem.checkEnabledFn(),
      ContextMenuItemStateCommandCriteria.MenuItem,
      ContextMenuItemStateCommandCriteria.Item,
    );
  }

  public reset(): void {
    let oldState = this.state;
    this.state = new BfxBlotterOrdersContextMenuItemCancelState();
    this.state.iconClass = "icon-menu-error";
    this.state.title = oldState.title;
  }

  private static checkAvailableFn(): ContextMenuItemStateCommand<CubiqTS.OrderDataImpl> {
    return function (
      this: void,
      menuState: BfxBlotterOrdersContextMenuState,
      itemState: ContextMenuItemState,
    ) {
      itemState.available =
        menuState.canCancel || menuState.canUnrestrictedCancelModify || menuState.canCancelHedge;
    };
  }

  private static checkEnabledFn(): ContextMenuItemStateCommand<CubiqTS.OrderDataImpl> {
    return function (
      this: void,
      menuState: BfxBlotterOrdersContextMenuState,
      itemState: BfxBlotterOrdersContextMenuItemCancelState,
      item: CubiqTS.OrderDataImpl,
    ) {
      if (!item) {
        itemState.enabled = false;
      } else {
        // Ensure right base cancel privilege is available for order type
        itemState.enabled =
          itemState.enabled && item.orderType === CubiqTS.OrderType.BMALGO
            ? menuState.canCancelHedge
            : menuState.canCancel;
        itemState.enabled =
          itemState.enabled &&
          BfxBlotterOrdersContextMenuCancelItem.checkOrderState(item, menuState);
        itemState.enabled =
          itemState.enabled &&
          BfxBlotterOrdersContextMenuCancelItem.checkMembership(item, menuState, itemState);
        itemState.enabled =
          itemState.enabled &&
          BfxBlotterOrdersContextMenuCancelItem.checkMultiClient(item, menuState, itemState);
        itemState.enabled =
          itemState.enabled && !BfxBlotterOrdersContextMenuCancelItem.checkOrderLocked(item);

        if (item.extendedInfo.has(PROP_POSTHARDCUTOFF)) {
          itemState.enabled = false;
        }

        if (CubiqTS.OrderState.FILL_PENDING == item.orderState) {
          itemState.enabled = false;
        }
      }
    };
  }

  private static checkMembership(
    item: CubiqTS.OrderDataImpl,
    menuState: BfxBlotterOrdersContextMenuState,
    itemState: BfxBlotterOrdersContextMenuItemCancelState,
  ): boolean {
    let enabled: boolean =
      item.entryUserId.toString() == menuState.activeUser.userId ||
      menuState.canUnrestrictedCancelModify;

    if (!enabled) {
      // For trader check if order is in trader group, sales group for sales user or clients for client user
      if (
        menuState.activeUser.userTypeId == CubiqTS.UserType.TRADER &&
        item.deskId != null &&
        !menuState.canUnrestrictedCancelModify
      ) {
        enabled = menuState.activeUser.tradingGroupsIdSet.has(item.deskId.toString());
      } else if (
        menuState.activeUser.userTypeId == CubiqTS.UserType.SALES &&
        item.salesGroupId != null &&
        !menuState.canUnrestrictedCancelModify
      ) {
        enabled = menuState.activeUser.salesGroupsIdSet.has(item.salesGroupId.toString());
      } else if (
        menuState.activeUser.userTypeId == CubiqTS.UserType.CLIENT &&
        item.clientCode != null
      ) {
        enabled = menuState.activeUser.clientIdSet.has(item.clientCode);
      }
    }

    return enabled;
  }

  private static checkMultiClient(
    item: CubiqTS.OrderDataImpl,
    menuState: BfxBlotterOrdersContextMenuState,
    itemState: BfxBlotterOrdersContextMenuItemCancelState,
  ): boolean {
    let enabled: boolean = true;

    if (itemState.clientCode == null) {
      itemState.clientCode = item.clientCode;
    } else if (!menuState.canCancelCrossClient && !menuState.canUnrestrictedCancelModify) {
      enabled = itemState.clientCode == item.clientCode;
    }

    return enabled;
  }

  private static checkOrderState(
    item: CubiqTS.OrderDataImpl,
    menuState: BfxBlotterOrdersContextMenuState,
  ): boolean {
    let enabled: boolean = false;

    switch (item.orderState) {
      case CubiqTS.OrderState.ACTIVE:
      case CubiqTS.OrderState.PENDING:
      case CubiqTS.OrderState.SUSPENDED:
      case CubiqTS.OrderState.PENDING_CANCELLATION:
        enabled = true;
        break;
      case CubiqTS.OrderState.TRIGGERED:
      case CubiqTS.OrderState.FILLING:
        let isTrader: boolean = menuState.activeUser.userTypeId == CubiqTS.UserType.TRADER;
        let isEntry: boolean = item.entryUserId.toString() === menuState.activeUser.userId;
        let isInTraderGroup: boolean =
          isTrader && menuState.activeUser.tradingGroupsIds.indexOf(item.deskId.toString()) >= 0;
        enabled =
          menuState.canUnrestrictedCancelModify || (isTrader && (isEntry || isInTraderGroup));
        break;
      default:
        enabled = false;
        break;
    }

    if (item.extendedInfo.has(PROP_POSTHARDCUTOFF)) {
      enabled = false;
    }

    // Order in transfer
    if (
      enabled &&
      item.targetDeskId != null &&
      menuState.traderGroups != null &&
      menuState.traderGroups.has(item.targetDeskId.toString())
    ) {
      let targetDesk: CubiqTS.TradingGroupImpl = menuState.traderGroups.get(
        item.targetDeskId.toString(),
      );

      // Check if order is in transfer to external group or pending expiry acceptance
      if (
        targetDesk.isExternallyFilledFg === true ||
        item.orderState === CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED
      ) {
        enabled = false;
      } else {
        // Order is pending transfer
        enabled = true;
      }
    }

    return enabled;
  }

  private static checkOrderLocked(item: CubiqTS.OrderDataImpl): boolean {
    return item.lockType != null;
  }

  public static get state(): any {
    return new BfxBlotterOrdersContextMenuItemCancelState();
  }
}
